import React, { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { theme, inputStyle } from '../../theme';
import SmartFindPanel from '../SmartFindPanel';

/**
 * Modal for booking an appointment from a calendar slot click.
 * Now with patient auto-search: type name, phone or UHID → dropdown of matching patients.
 *
 * Props:
 *   slotClick, setSlotClick, calBookForm, setCalBookForm,
 *   handleCalBook, onNewPatient, patientsList, treatmentTypes, API_BASE
 */
const SlotBookingModal = ({
  slotClick, setSlotClick, calBookForm, setCalBookForm,
  handleCalBook, onNewPatient, patientsList = [], treatmentTypes = [],
  API_BASE,
}) => {
  const [showSmart, setShowSmart] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const inputRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (slotClick) {
      setSearchQuery('');
      setSuggestions([]);
      setSelectedPatient(null);
      setShowSuggestions(false);
      setShowSmart(false);
    }
  }, [slotClick]);

  // Search patients by name, phone, UHID
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const q = searchQuery.toLowerCase().trim();
    const matched = (patientsList || []).filter(p => {
      const name = ((p.title ? p.title + ' ' : '') + p.name).toLowerCase();
      const phone = (p.phone || '').toLowerCase();
      const uhid = (p.uhid || '').toLowerCase();
      return name.includes(q) || phone.includes(q) || uhid.includes(q);
    }).slice(0, 8);
    setSuggestions(matched);
    setShowSuggestions(matched.length > 0);
  }, [searchQuery, patientsList]);

  const selectPatient = (pt) => {
    const fullName = (pt.title ? pt.title + ' ' : '') + pt.name;
    setSelectedPatient(pt);
    setSearchQuery(fullName);
    setShowSuggestions(false);
    setCalBookForm(f => ({
      ...f,
      patientName: fullName,
      phone: pt.phone || '',
      patientId: pt._id,
    }));
  };

  const clearSelection = () => {
    setSelectedPatient(null);
    setSearchQuery('');
    setCalBookForm(f => ({ ...f, patientName: '', phone: '', patientId: undefined }));
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (!slotClick) return null;

  return (
    <div onClick={() => setSlotClick(null)}
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '16px', padding: '30px', width: '500px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', position: 'relative' }}>

        <button onClick={() => setSlotClick(null)}
          style={{ position: 'absolute', top: '14px', right: '16px', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#aaa', lineHeight: 1 }}>✕</button>

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 4px', color: theme.navy, fontSize: '1.2rem' }}>New Appointment</h3>
          <div style={{ fontSize: '0.85rem', color: '#888' }}>
            {new Date(slotClick.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            &nbsp;•&nbsp; <strong style={{ color: theme.primaryDark }}>{slotClick.time}</strong>
          </div>
        </div>

        {/* Patient Search */}
        <div style={{ marginBottom: '12px', position: 'relative' }}>
          <label style={{ fontSize: '0.82rem', color: '#555', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
            Patient (search by Name / Phone / UHID) *
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {selectedPatient ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', backgroundColor: '#e8f8f5', border: `2px solid ${theme.primary}`, borderRadius: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', color: theme.navy, fontSize: '0.95rem' }}>
                    {(selectedPatient.title ? selectedPatient.title + ' ' : '') + selectedPatient.name}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#666' }}>
                    {selectedPatient.uhid} &nbsp;•&nbsp; {selectedPatient.phone || 'No phone'}
                    {selectedPatient.age ? ` • ${selectedPatient.age}yr` : ''}
                    {selectedPatient.gender ? ` / ${selectedPatient.gender}` : ''}
                  </div>
                </div>
                <button onClick={clearSelection}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#999' }}>✕</button>
              </div>
            ) : (
              <input ref={inputRef} placeholder="Type patient name, phone or UHID..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setCalBookForm(f => ({ ...f, patientName: e.target.value }));
                }}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                autoFocus
                style={{ ...inputStyle, flex: 1 }} />
            )}
            <button onClick={() => { setSlotClick(null); onNewPatient && onNewPatient(); }}
              title="Register a new patient"
              style={{ padding: '10px 14px', backgroundColor: theme.success, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              + New PT
            </button>
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && !selectedPatient && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 60, zIndex: 10,
              backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: '280px', overflowY: 'auto', marginTop: '4px',
            }}>
              {suggestions.map(pt => {
                const fullName = (pt.title ? pt.title + ' ' : '') + pt.name;
                return (
                  <div key={pt._id}
                    onClick={() => selectPatient(pt)}
                    style={{
                      padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                      transition: '0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e8f4fd'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: theme.navy, fontSize: '0.9rem' }}>{fullName}</div>
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>
                          {pt.uhid} &nbsp;•&nbsp; {pt.phone || 'No phone'}
                          {pt.age ? ` • ${pt.age}yr` : ''}
                          {pt.gender ? ` / ${pt.gender}` : ''}
                        </div>
                      </div>
                      {pt.photo && (
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden',
                          border: '1px solid #dee2e6', flexShrink: 0,
                        }}>
                          <img src={pt.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => e.target.style.display = 'none'} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: '8px 14px', fontSize: '0.75rem', color: '#999', textAlign: 'center', borderTop: '1px solid #eee' }}>
                {suggestions.length} patient{suggestions.length !== 1 ? 's' : ''} found — or type a new name
              </div>
            </div>
          )}
        </div>

        {/* Phone (auto-filled or manual) */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '0.82rem', color: '#555', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Phone</label>
          <input placeholder="Mobile number"
            value={calBookForm.phone}
            onChange={e => setCalBookForm({ ...calBookForm, phone: e.target.value })}
            disabled={!!selectedPatient}
            style={{ ...inputStyle, backgroundColor: selectedPatient ? '#f8f9fa' : 'white' }} />
        </div>

        {/* Time */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '0.82rem', color: '#555', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Time *</label>
          <input type="time"
            value={calBookForm.time || slotClick.time}
            onChange={e => setCalBookForm({ ...calBookForm, time: e.target.value })}
            style={inputStyle} />
        </div>

        {/* Treatment — dropdown from treatmentTypes + free text */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '0.82rem', color: '#555', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Treatment (optional)</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select value={calBookForm.treatment}
              onChange={e => setCalBookForm({ ...calBookForm, treatment: e.target.value })}
              style={{ ...inputStyle, flex: 1 }}>
              <option value="">-- Select treatment --</option>
              {(treatmentTypes || []).map(tt => (
                <option key={tt._id || tt.name} value={tt.name}>{tt.name}</option>
              ))}
            </select>
            <input placeholder="or type..."
              value={calBookForm.treatment}
              onChange={e => setCalBookForm({ ...calBookForm, treatment: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') handleCalBook(); }}
              style={{ ...inputStyle, width: '140px' }} />
          </div>
        </div>

        {/* Selected patient summary */}
        {selectedPatient && (
          <div style={{
            padding: '10px 14px', backgroundColor: '#f8f9fa', borderRadius: '8px',
            marginBottom: '16px', border: '1px solid #e9ecef', fontSize: '0.82rem',
          }}>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {selectedPatient.age && <span><strong>Age:</strong> {selectedPatient.age} yrs</span>}
              {selectedPatient.gender && <span><strong>Gender:</strong> {selectedPatient.gender}</span>}
              {selectedPatient.address && <span><strong>Address:</strong> {selectedPatient.address}</span>}
              {(selectedPatient.medicalHistory || []).length > 0 && (
                <span style={{ color: '#dc2626' }}><strong>Medical:</strong> {selectedPatient.medicalHistory.join(', ')}</span>
              )}
              {(selectedPatient.allergies || []).length > 0 && (
                <span style={{ color: '#ea580c' }}><strong>Allergies:</strong> {selectedPatient.allergies.join(', ')}</span>
              )}
            </div>
          </div>
        )}

        {/* Smart-find inline — lets user swap this slot for an AI-ranked alternative
            without leaving the modal. Uses the same /smart-schedule/suggest endpoint
            as the standalone page; mobile app is untouched. */}
        {API_BASE && (
          <div style={{ marginBottom: 14 }}>
            <button type="button" onClick={() => setShowSmart(v => !v)}
              style={{
                background: 'none', border: `1.5px dashed ${theme.primary}`,
                padding: '8px 12px', borderRadius: 9, cursor: 'pointer',
                color: theme.primaryDark, fontWeight: 700, fontSize: '0.82rem',
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
              <Sparkles size={14}/>
              {showSmart ? 'Hide Smart Suggestions' : 'Suggest better slots (AI-ranked)'}
            </button>
            {showSmart && (
              <div style={{ marginTop: 10 }}>
                <SmartFindPanel
                  API_BASE={API_BASE}
                  patientsList={patientsList}
                  treatmentTypes={treatmentTypes}
                  compact
                  initialTreatment={calBookForm.treatment || ''}
                  initialDate={slotClick.date}
                  onPick={(s) => {
                    setCalBookForm(f => ({
                      ...f,
                      time: s.time,
                      treatment: s.treatment || f.treatment || '',
                      doctor: s.doctorName || f.doctor || '',
                      doctorId: s.doctorId || f.doctorId,
                      duration: s.duration || f.duration || 30,
                    }));
                    // Shift the modal's date if the user picked a different day.
                    if (s.date !== slotClick.date) {
                      setSlotClick({ date: s.date, time: s.time });
                    }
                    setShowSmart(false);
                  }}
                />
              </div>
            )}
          </div>
        )}

        <button onClick={handleCalBook}
          style={{ width: '100%', padding: '13px', backgroundColor: theme.primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', letterSpacing: '0.02em' }}>
          Book Appointment
        </button>
      </div>
    </div>
  );
};

export default SlotBookingModal;
